import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface HolidayManagementProps {
  domain: string;
}

interface Holiday {
  id: string;
  title: string;
  date: string;
  type: 'public' | 'school' | 'optional';
  description: string;
  year: string;
  month: string;
  day: string;
  createdAt: string;
  createdBy: string;
}

const HolidayManagement: React.FC<HolidayManagementProps> = ({ domain }) => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [showForm, setShowForm] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - 1 + i).toString());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const [holidayForm, setHolidayForm] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    type: 'school' as 'public' | 'school' | 'optional',
    description: '',
  });

  useEffect(() => {
    loadHolidays();
  }, [domain, selectedYear]);

  const loadHolidays = async () => {
    setLoading(true);
    try {
      const holidaysRef = collection(db, `school-data/${domain}/holidays`);
      const snapshot = await getDocs(holidaysRef);

      const holidaysList: Holiday[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Holiday));

      // Filter by selected year
      const filteredHolidays = holidaysList.filter(h => h.year === selectedYear);

      setHolidays(filteredHolidays.sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      ));
    } catch (error: any) {
      console.error('Error loading holidays:', error);
      alert(`Failed to load holidays: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setHolidayForm({ ...holidayForm, [field]: value });
  };

  const handleAddHoliday = async () => {
    if (!holidayForm.title || !holidayForm.date) {
      alert('Please fill in all required fields!');
      return;
    }

    setLoading(true);
    try {
      const userDataString = localStorage.getItem('userData');
      const currentUser = userDataString ? JSON.parse(userDataString) : null;
      const currentUserEmail = currentUser?.email || 'admin';

      const holidayDate = new Date(holidayForm.date);
      const holidayId = `holiday_${Date.now()}`;

      const holidayData: Holiday = {
        id: holidayId,
        title: holidayForm.title,
        date: holidayForm.date,
        type: holidayForm.type,
        description: holidayForm.description,
        year: holidayDate.getFullYear().toString(),
        month: holidayDate.toLocaleString('default', { month: 'long' }),
        day: holidayDate.getDate().toString(),
        createdAt: new Date().toISOString(),
        createdBy: currentUserEmail,
      };

      await setDoc(doc(db, `school-data/${domain}/holidays`, holidayId), holidayData);

      alert('Holiday added successfully!');
      resetForm();
      loadHolidays();
      setShowForm(false);
    } catch (error: any) {
      console.error('Error adding holiday:', error);
      alert(`Failed to add holiday: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateHoliday = async () => {
    if (!editingHoliday) return;

    setLoading(true);
    try {
      const holidayDate = new Date(holidayForm.date);
      const holidayRef = doc(db, `school-data/${domain}/holidays`, editingHoliday.id);

      await updateDoc(holidayRef, {
        title: holidayForm.title,
        date: holidayForm.date,
        type: holidayForm.type,
        description: holidayForm.description,
        year: holidayDate.getFullYear().toString(),
        month: holidayDate.toLocaleString('default', { month: 'long' }),
        day: holidayDate.getDate().toString(),
      });

      alert('Holiday updated successfully!');
      setEditingHoliday(null);
      resetForm();
      loadHolidays();
      setShowForm(false);
    } catch (error: any) {
      console.error('Error updating holiday:', error);
      alert(`Failed to update holiday: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async (holidayId: string) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, `school-data/${domain}/holidays`, holidayId));
      alert('Holiday deleted successfully!');
      loadHolidays();
    } catch (error: any) {
      console.error('Error deleting holiday:', error);
      alert(`Failed to delete holiday: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setHolidayForm({
      title: holiday.title,
      date: holiday.date,
      type: holiday.type,
      description: holiday.description,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setHolidayForm({
      title: '',
      date: new Date().toISOString().split('T')[0],
      type: 'school',
      description: '',
    });
    setEditingHoliday(null);
  };

  const downloadHolidayList = () => {
    const reportData = holidays.map(h => ({
      'Date': new Date(h.date).toLocaleDateString('en-IN'),
      'Day': new Date(h.date).toLocaleDateString('en-IN', { weekday: 'long' }),
      'Holiday': h.title,
      'Type': h.type.toUpperCase(),
      'Description': h.description || '',
    }));

    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Holidays');

    XLSX.writeFile(wb, `holidays_${selectedYear}.xlsx`);
  };

  // Group holidays by month
  const holidaysByMonth = holidays.reduce((acc, holiday) => {
    const month = holiday.month;
    if (!acc[month]) acc[month] = [];
    acc[month].push(holiday);
    return acc;
  }, {} as Record<string, Holiday[]>);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-calendar-event me-2 text-primary-custom"></i>
              Holiday Management
            </h5>
            <small className="text-muted">Manage school and public holidays</small>
          </div>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{ width: '120px' }}
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              className="btn btn-success btn-sm"
              onClick={downloadHolidayList}
              disabled={holidays.length === 0}
            >
              <i className="bi bi-download me-1"></i>
              Export
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Holiday
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {/* Add/Edit Form */}
        {showForm && (
          <div className="card mb-4 bg-light">
            <div className="card-body">
              <h6 className="mb-3">
                {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Holiday Title *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={holidayForm.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    placeholder="e.g., Independence Day"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={holidayForm.date}
                    onChange={(e) => handleFormChange('date', e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Type *</label>
                  <select
                    className="form-select"
                    value={holidayForm.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                  >
                    <option value="school">School Holiday</option>
                    <option value="public">Public Holiday</option>
                    <option value="optional">Optional Holiday</option>
                  </select>
                </div>

                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={holidayForm.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder="Optional description"
                  />
                </div>

                <div className="col-md-12">
                  <button
                    className="btn btn-primary me-2"
                    onClick={editingHoliday ? handleUpdateHoliday : handleAddHoliday}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : (editingHoliday ? 'Update Holiday' : 'Add Holiday')}
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => { setShowForm(false); resetForm(); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !showForm && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading holidays...</p>
          </div>
        )}

        {/* Holidays Display */}
        {!loading && (
          <>
            {/* Summary */}
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="text-muted mb-1 small">Total Holidays</h6>
                    <h4 className="mb-0">{holidays.length}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-danger bg-opacity-10">
                  <div className="card-body">
                    <h6 className="text-danger mb-1 small">Public Holidays</h6>
                    <h4 className="mb-0 text-danger">
                      {holidays.filter(h => h.type === 'public').length}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-primary bg-opacity-10">
                  <div className="card-body">
                    <h6 className="text-primary mb-1 small">School Holidays</h6>
                    <h4 className="mb-0 text-primary">
                      {holidays.filter(h => h.type === 'school').length}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-warning bg-opacity-10">
                  <div className="card-body">
                    <h6 className="text-warning mb-1 small">Optional Holidays</h6>
                    <h4 className="mb-0 text-warning">
                      {holidays.filter(h => h.type === 'optional').length}
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Holidays List by Month */}
            {holidays.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-calendar-x display-1 text-muted"></i>
                <p className="mt-3 text-muted">No holidays added for {selectedYear}.</p>
                <button
                  className="btn btn-primary mt-2"
                  onClick={() => setShowForm(true)}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Add First Holiday
                </button>
              </div>
            ) : (
              <div className="row">
                {months.map((month) => {
                  const monthHolidays = holidaysByMonth[month];
                  if (!monthHolidays || monthHolidays.length === 0) return null;

                  return (
                    <div key={month} className="col-md-6 mb-4">
                      <div className="card">
                        <div className="card-header bg-primary-custom text-white">
                          <h6 className="mb-0">
                            <i className="bi bi-calendar3 me-2"></i>
                            {month} {selectedYear}
                          </h6>
                        </div>
                        <div className="card-body p-0">
                          <div className="list-group list-group-flush">
                            {monthHolidays.map((holiday) => (
                              <div key={holiday.id} className="list-group-item">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      <strong className="text-primary">
                                        {new Date(holiday.date).getDate()}
                                      </strong>
                                      <h6 className="mb-0">{holiday.title}</h6>
                                      {holiday.type === 'public' && (
                                        <span className="badge bg-danger">Public</span>
                                      )}
                                      {holiday.type === 'school' && (
                                        <span className="badge bg-primary">School</span>
                                      )}
                                      {holiday.type === 'optional' && (
                                        <span className="badge bg-warning">Optional</span>
                                      )}
                                    </div>
                                    <small className="text-muted d-block">
                                      {new Date(holiday.date).toLocaleDateString('en-IN', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                      })}
                                    </small>
                                    {holiday.description && (
                                      <small className="text-muted d-block mt-1">
                                        {holiday.description}
                                      </small>
                                    )}
                                  </div>
                                  <div className="btn-group btn-group-sm">
                                    <button
                                      className="btn btn-outline-primary"
                                      onClick={() => handleEdit(holiday)}
                                      title="Edit"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => handleDeleteHoliday(holiday.id)}
                                      title="Delete"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default HolidayManagement;
